import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import Cartao from '@/components/ui/Cartao';
import Chip from '@/components/ui/Chip';
import Botao from '@/components/ui/Botao';
import EstadoVazio from '@/components/ui/EstadoVazio';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import ModalConfirmar from '@/pages/gerenciar/loja/components/ModalConfirmar';
import FolhaChamar from './FolhaChamar';
import FolhaFuncoes, { type FuncaoRemovivel } from './FolhaFuncoes';
import FolhaTrocarCaixa from './FolhaTrocarCaixa';
import {
  formatarTelefone,
  horaBrasilia,
  iniciais,
  linkWhatsApp,
  mensagemConviteExistente,
  noFuturo,
  normalizarCaixa,
  normalizarConvite,
  normalizarMembro,
  normalizarOperador,
  normalizarPonto,
  normalizarPortaria,
  normalizarQuem,
  type CaixaEquipe,
  type ConviteLinha,
  type MembroDirecao,
  type Notificar,
  type OperadorLinha,
  type PontoEquipe,
  type PortariaLinha,
  type QuemLinha,
} from './utils';

type Bruto = Record<string, unknown>;

interface Props {
  eventoId: number;
  eventoNome: string;
  aoContagem: (n: number) => void;
  notificar: Notificar;
}

interface PessoaResumo {
  chave: string;
  nome: string;
  email: string;
  funcoes: FuncaoRemovivel[];
}

function chaveDe(nome: string, email: string): string {
  return (email || nome || '?').toLowerCase().trim();
}

function rotuloLinha(l: OperadorLinha): string {
  if (l.ponto_id == null && l.caixa) return 'Caixa';
  if (l.ponto_id != null) return `Barraca ${l.ponto_nome || 'sem nome'}`;
  return 'Todas as barracas';
}

function TileNumero({
  valor,
  rotulo,
  cor,
}: {
  valor: number;
  rotulo: string;
  cor: string;
}) {
  return (
    <div className="flex flex-col items-start rounded-2xl bg-white p-3 text-left shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <span className={`text-[26px] font-extrabold leading-none ${cor}`}>{valor}</span>
      <span className="mt-1 text-[12px] font-medium leading-tight text-foreground-500">{rotulo}</span>
    </div>
  );
}

function LinhaPessoa({
  nome,
  email,
  legenda,
  aoAbrir,
}: {
  nome: string;
  email: string;
  legenda?: string;
  aoAbrir: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoAbrir}
      className="flex min-h-[48px] w-full cursor-pointer items-center gap-3 rounded-xl px-1 py-1.5 text-left transition-colors hover:bg-background-100"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[13px] font-bold text-primary-700">
        {iniciais(nome, email)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-foreground-950">
          {nome || email}
        </span>
        {legenda && <span className="block text-[12px] text-foreground-500">{legenda}</span>}
      </span>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400">
        <i className="ri-arrow-right-s-line" />
      </span>
    </button>
  );
}

export default function VisaoEquipe({ eventoId, eventoNome, aoContagem, notificar }: Props) {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [erroPontos, setErroPontos] = useState<string | null>(null);
  const [erroCaixas, setErroCaixas] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [pontos, setPontos] = useState<PontoEquipe[]>([]);
  const [caixas, setCaixas] = useState<CaixaEquipe[]>([]);
  const [operadores, setOperadores] = useState<OperadorLinha[]>([]);
  const [portaria, setPortaria] = useState<PortariaLinha[]>([]);
  const [convites, setConvites] = useState<ConviteLinha[]>([]);
  const [direcao, setDirecao] = useState<MembroDirecao[]>([]);
  const [quem, setQuem] = useState<QuemLinha[]>([]);

  const [chamar, setChamar] = useState<{ caixa?: boolean; pontos?: number[]; portaria?: boolean } | null>(
    null,
  );
  const [folhaPessoa, setFolhaPessoa] = useState<PessoaResumo | null>(null);
  const [cancelando, setCancelando] = useState<ConviteLinha | null>(null);
  const [cancelandoAgora, setCancelandoAgora] = useState(false);
  const [liberando, setLiberando] = useState<ConviteLinha | null>(null);
  const [liberandoAgora, setLiberandoAgora] = useState(false);
  const [trocando, setTrocando] = useState<ConviteLinha | null>(null);
  const [copiado, setCopiado] = useState<number | null>(null);

  const recarregar = useCallback(() => setReload((r) => r + 1), []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setErro(null);
      setErroPontos(null);
      setErroCaixas(null);
      setCarregando(true);
      try {
        const [rPt, rCx, rOp, rPo, rCo, rDir, rQ] = await Promise.all([
          supabase.rpc('loja_pontos_do_evento', { p_evento: eventoId }),
          supabase.rpc('caixa_caixas_do_evento', { p_evento: eventoId }),
          supabase.rpc('loja_operadores_do_evento', { p_evento: eventoId }),
          supabase.rpc('equipe_portaria_do_evento', { p_evento: eventoId }),
          supabase.rpc('equipe_convites_do_evento', { p_evento: eventoId }),
          supabase
            .from('vw_equipe_evento')
            .select('id, pessoa, email, funcao, status')
            .eq('evento_id', eventoId),
          supabase.rpc('equipe_quem_e_quem', { p_evento: eventoId }),
        ]);
        // Erros essenciais: sem estes não dá para montar a tela.
        if (rOp.error) throw rOp.error;
        if (rPo.error) throw rPo.error;
        if (rCo.error) throw rCo.error;
        if (rDir.error) throw rDir.error;

        if (!ativo) return;
        // Barracas e caixa: se falharem, avisamos só no bloco delas.
        if (rPt.error) {
          setErroPontos(mensagemBanco(rPt.error));
          setPontos([]);
        } else {
          setPontos(((rPt.data as Bruto[]) || []).map(normalizarPonto));
        }
        if (rCx.error) {
          setErroCaixas(mensagemBanco(rCx.error));
          setCaixas([]);
        } else {
          setCaixas(((rCx.data as Bruto[]) || []).map(normalizarCaixa));
        }
        setOperadores(((rOp.data as Bruto[]) || []).map(normalizarOperador));
        setPortaria(((rPo.data as Bruto[]) || []).map(normalizarPortaria));
        setConvites(((rCo.data as Bruto[]) || []).map(normalizarConvite));
        setDirecao(((rDir.data as Bruto[]) || []).map(normalizarMembro));
        // Quem é quem: se falhar, a lista continua como hoje (sem os itens novos).
        if (rQ.error) setQuem([]);
        else setQuem(((rQ.data as Bruto[]) || []).map(normalizarQuem));
      } catch (e) {
        if (ativo) setErro(mensagemBanco(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    void carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId, reload]);

  const pontosAtivos = useMemo(() => pontos.filter((p) => p.ativo), [pontos]);
  const caixasAtivos = useMemo(() => caixas.filter((c) => c.ativo), [caixas]);

  const temCaixa = caixasAtivos.length > 0;
  const temBarracas = pontosAtivos.length > 0;
  const temLoja = temCaixa || temBarracas;

  const pessoasCaixa = useMemo(() => {
    const mapa = new Map<string, OperadorLinha>();
    operadores
      .filter((l) => l.caixa)
      .forEach((l) => {
        const k = chaveDe(l.nome, l.email);
        if (!mapa.has(k)) mapa.set(k, l);
      });
    return [...mapa.values()];
  }, [operadores]);

  const convitesAguardando = useMemo(
    () => convites.filter((c) => c.situacao === 'aguardando'),
    [convites],
  );

  const quemPorConvite = useMemo(() => {
    const m = new Map<number, QuemLinha>();
    quem.forEach((q) => m.set(q.convite_id, q));
    return m;
  }, [quem]);

  // "No celular": quem abriu o link neste celular e está com a função ativa.
  const noCelular = useMemo(
    () => quem.filter((q) => q.como === 'link' && q.situacao === 'aceito').length,
    [quem],
  );

  const semNgm = useMemo(() => {
    return pontosAtivos.filter(
      (p) => !operadores.some((l) => l.ponto_id === p.ponto_id || l.ponto_id === null),
    );
  }, [pontosAtivos, operadores]);

  const pessoas = useMemo(() => {
    const mapa = new Map<string, PessoaResumo>();
    const pega = (nome: string, email: string): PessoaResumo => {
      const k = chaveDe(nome, email);
      let p = mapa.get(k);
      if (!p) {
        p = { chave: k, nome: nome || email, email, funcoes: [] };
        mapa.set(k, p);
      }
      return p;
    };
    operadores.forEach((l) => {
      const p = pega(l.nome, l.email);
      p.funcoes.push({
        chave: `op-${l.operador_id}`,
        rotulo: rotuloLinha(l),
        operador_id: l.operador_id,
        portaria_id: null,
      });
    });
    portaria.forEach((pt) => {
      const p = pega(pt.nome, pt.email);
      p.funcoes.push({
        chave: `po-${pt.portaria_id}`,
        rotulo: 'Portaria',
        operador_id: null,
        portaria_id: pt.portaria_id,
      });
    });
    return mapa;
  }, [operadores, portaria]);

  useEffect(() => {
    if (!carregando) aoContagem(convitesAguardando.length);
  }, [carregando, convitesAguardando.length, aoContagem]);

  function abrirPessoa(nome: string, email: string) {
    const p = pessoas.get(chaveDe(nome, email));
    if (p) setFolhaPessoa(p);
  }

  function enviarWhatsConvite(c: ConviteLinha) {
    const texto = c.link
      ? mensagemConviteExistente({
          nome: c.nome,
          evento: eventoNome,
          funcoes: c.funcoes,
          link: c.link,
        })
      : '';
    window.open(linkWhatsApp(c.telefone, texto), '_blank', 'noopener,noreferrer');
  }

  async function copiarConvite(c: ConviteLinha) {
    try {
      await navigator.clipboard.writeText(c.link);
    } catch {
      /* silencioso */
    }
    setCopiado(c.convite_id);
    setTimeout(() => setCopiado(null), 2000);
  }

  async function cancelarConvite() {
    if (!cancelando) return;
    setCancelandoAgora(true);
    try {
      const { data, error } = await supabase.rpc('equipe_cancelar_convite', {
        p_convite: cancelando.convite_id,
      });
      if (error) throw error;
      const resp = data as { ok?: boolean; erro?: string } | null;
      if (resp && resp.ok === false) {
        notificar(resp.erro || 'Não foi possível cancelar.', 'erro');
        setCancelando(null);
        return;
      }
      notificar('Convite cancelado.');
      setCancelando(null);
      recarregar();
    } catch (e) {
      notificar(mensagemBanco(e), 'erro');
      setCancelando(null);
    } finally {
      setCancelandoAgora(false);
    }
  }

  async function liberarOutroCelular() {
    if (!liberando) return;
    setLiberandoAgora(true);
    try {
      const { data, error } = await supabase.rpc('equipe_liberar_outro_celular', {
        p_convite: liberando.convite_id,
      });
      if (error) throw error;
      const resp = data as { ok?: boolean; mensagem?: string; erro?: string } | null;
      if (resp && resp.ok === false) {
        notificar(resp.erro || 'Não foi possível liberar.', 'erro');
      } else {
        notificar(resp?.mensagem || 'Link liberado para outro celular.');
        recarregar();
      }
    } catch (e) {
      notificar(mensagemBanco(e), 'erro');
    } finally {
      setLiberandoAgora(false);
      setLiberando(null);
    }
  }

  if (carregando) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 esqueleto rounded-2xl bg-secondary-100" />
          ))}
        </div>
        <div className="h-40 esqueleto rounded-3xl bg-secondary-100" />
        <div className="h-40 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <Cartao className="p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={recarregar}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </Cartao>
    );
  }

  const avisos: string[] = [];
  if (convitesAguardando.length > 0) {
    avisos.push(
      convitesAguardando.length === 1
        ? '1 convite ainda não aceito'
        : `${convitesAguardando.length} convites ainda não aceitos`,
    );
  }
  semNgm.forEach((p) => avisos.push(`A barraca ${p.nome} está sem ninguém`));
  if (temCaixa && pessoasCaixa.length === 0) avisos.push('Ninguém no caixa');

  // Só é "ninguém na equipe" quando tudo respondeu sem erro e veio vazio.
  const semNada =
    !erroPontos &&
    !erroCaixas &&
    pontosAtivos.length === 0 &&
    caixasAtivos.length === 0 &&
    operadores.length === 0 &&
    portaria.length === 0 &&
    convites.length === 0;

  return (
    <div className="space-y-4">
      {semNada ? (
        <div className="space-y-4">
          <EstadoVazio
            icone="ri-team-line"
            titulo="Ninguém na equipe ainda"
            descricao="Toque em + Chamar pessoa para montar a equipe do dia com um link no WhatsApp."
            acaoRotulo="Chamar pessoa"
            aoClicarAcao={() => setChamar({})}
          />
          <DicaPrimeiraVez id="painel.equipe.vazio" />
        </div>
      ) : (
        <>
      {/* Tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <TileNumero valor={pessoasCaixa.length} rotulo="Caixa" cor="text-primary-600" />
        <TileNumero valor={pontosAtivos.length} rotulo="Barracas" cor="text-accent-600" />
        <TileNumero valor={portaria.length} rotulo="Portaria" cor="text-info-600" />
        <TileNumero valor={convitesAguardando.length} rotulo="Convites aguardando" cor="text-warning-600" />
        <TileNumero valor={noCelular} rotulo="No celular" cor="text-success-600" />
      </div>

      {avisos.length > 0 && (
        <div className="rounded-2xl border border-warning-200 bg-warning-50 px-4 py-3">
          <ul className="space-y-1">
            {avisos.map((a) => (
              <li key={a} className="flex items-start gap-2 text-[13px] font-medium text-warning-800">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                  <i className="ri-alert-line" />
                </span>
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Botao cheio icone="ri-user-add-line" onClick={() => setChamar({})}>
        Chamar pessoa
      </Botao>

      <Botao
        variante="secundario"
        cheio
        icone="ri-printer-line"
        onClick={() => navigate(`/gerenciar/${eventoId}/cartazes`)}
      >
        Imprimir cartazes
      </Botao>

      {/* Caixa */}
      {(temCaixa || erroCaixas) && (
        <Cartao className="p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-[16px] font-bold text-foreground-950">
              <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                <i className="ri-money-dollar-circle-line" />
              </span>
              Caixa
            </h2>
            <Botao tamanho="md" variante="secundario" icone="ri-printer-line" onClick={() => navigate(`/gerenciar/${eventoId}/cartazes?tipo=caixa`)}>
              Cartaz
            </Botao>
            <Botao tamanho="md" variante="secundario" icone="ri-add-line" onClick={() => setChamar({ caixa: true })}>
              Chamar
            </Botao>
          </div>

          {erroCaixas ? (
            <div className="mt-2">
              <p className="text-[13px] text-danger-600">{erroCaixas}</p>
              <button
                type="button"
                onClick={recarregar}
                className="mt-2 cursor-pointer whitespace-nowrap text-[13px] font-semibold text-primary-700 underline"
              >
                Tentar de novo
              </button>
            </div>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap gap-2">
                {caixasAtivos.length === 0 ? (
                  <span className="text-[13px] text-foreground-500">Nenhum caixa criado neste evento.</span>
                ) : (
                  caixasAtivos.map((c) => (
                    <span
                      key={c.caixa}
                      className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-[13px] font-semibold text-secondary-700"
                    >
                      <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                        <i className="ri-computer-line" />
                      </span>
                      {c.nome} · {c.terminal_apelido || 'fichas de papel'}
                    </span>
                  ))
                )}
              </div>

              <div className="mt-3">
                {pessoasCaixa.length === 0 ? (
                  <p className="text-[13px] text-foreground-500">Ninguém pode operar o caixa ainda.</p>
                ) : (
                  <>
                    <ul>
                      {pessoasCaixa.map((p) => (
                        <li key={`${p.operador_id}-${p.email}`}>
                          <LinhaPessoa
                            nome={p.nome}
                            email={p.email}
                            aoAbrir={() => abrirPessoa(p.nome, p.email)}
                          />
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 text-[12px] text-foreground-500">podem operar qualquer caixa do evento</p>
                  </>
                )}
              </div>
            </>
          )}
        </Cartao>
      )}

      {/* Barracas */}
      {erroPontos && (
        <Cartao className="p-4">
          <h2 className="flex items-center gap-2 text-[16px] font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center text-accent-600">
              <i className="ri-store-2-line" />
            </span>
            Barracas
          </h2>
          <p className="mt-2 text-[13px] text-danger-600">{erroPontos}</p>
          <button
            type="button"
            onClick={recarregar}
            className="mt-2 cursor-pointer whitespace-nowrap text-[13px] font-semibold text-primary-700 underline"
          >
            Tentar de novo
          </button>
        </Cartao>
      )}
      {!erroPontos && pontosAtivos.map((ponto) => {
        const diretas = operadores.filter((l) => l.ponto_id === ponto.ponto_id);
        const todas = operadores.filter((l) => l.ponto_id === null);
        const total = new Set([...diretas, ...todas].map((l) => chaveDe(l.nome, l.email))).size;
        return (
          <Cartao key={ponto.ponto_id} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex min-w-0 items-center gap-2 text-[16px] font-bold text-foreground-950">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-accent-600">
                  <i className="ri-store-2-line" />
                </span>
                <span className="truncate">{ponto.nome}</span>
              </h2>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <Chip tipo={total > 0 ? 'success' : 'warning'}>{total > 0 ? `${total} pessoas` : 'ninguém'}</Chip>
                <Botao tamanho="md" variante="secundario" icone="ri-printer-line" onClick={() => navigate(`/gerenciar/${eventoId}/cartazes?tipo=barraca&ponto=${ponto.ponto_id}`)}>
                  Cartaz
                </Botao>
                <Botao tamanho="md" variante="secundario" icone="ri-add-line" onClick={() => setChamar({ pontos: [ponto.ponto_id] })}>
                  Chamar
                </Botao>
              </div>
            </div>

            {(diretas.length > 0 || todas.length > 0) && (
              <ul className="mt-2">
                {diretas.map((l) => (
                  <li key={`d-${l.operador_id}`}>
                    <LinhaPessoa nome={l.nome} email={l.email} aoAbrir={() => abrirPessoa(l.nome, l.email)} />
                  </li>
                ))}
                {todas.map((l) => (
                  <li key={`t-${l.operador_id}`}>
                    <LinhaPessoa
                      nome={l.nome}
                      email={l.email}
                      legenda="todas"
                      aoAbrir={() => abrirPessoa(l.nome, l.email)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Cartao>
        );
      })}

      {/* Portaria */}
      <Cartao className="p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-[16px] font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center text-info-600">
              <i className="ri-door-open-line" />
            </span>
            Portaria
          </h2>
          <Botao tamanho="md" variante="secundario" icone="ri-printer-line" onClick={() => navigate(`/gerenciar/${eventoId}/cartazes?tipo=portaria`)}>
            Cartaz
          </Botao>
          <Botao tamanho="md" variante="secundario" icone="ri-add-line" onClick={() => setChamar({ portaria: true })}>
            Chamar
          </Botao>
        </div>
        {portaria.length === 0 ? (
          <p className="mt-2 text-[13px] text-foreground-500">Ninguém na portaria ainda.</p>
        ) : (
          <ul className="mt-2">
            {portaria.map((pt) => (
              <li key={pt.portaria_id}>
                <LinhaPessoa nome={pt.nome} email={pt.email} aoAbrir={() => abrirPessoa(pt.nome, pt.email)} />
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {/* Convites */}
      {convites.length > 0 && (
        <Cartao className="p-4">
          <h2 className="flex items-center gap-2 text-[16px] font-bold text-foreground-950">
            <span className="flex h-6 w-6 items-center justify-center text-warning-600">
              <i className="ri-mail-send-line" />
            </span>
            Convites
          </h2>
          <ul className="mt-2 space-y-3">
            {convites.map((c) => {
              const q = quemPorConvite.get(c.convite_id);
              const situacao = q?.situacao ?? c.situacao;
              const como = q?.como ?? null;
              const pessoa = q?.pessoa || c.nome;
              const funcao = q?.funcao || c.funcoes;
              const liberadoAgora = q?.liberado_ate ? noFuturo(q.liberado_ate) : false;
              return (
                <li key={c.convite_id} className="rounded-2xl bg-background-100 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-semibold text-foreground-950">{pessoa}</p>
                      {funcao && <p className="mt-0.5 text-[13px] text-foreground-500">{funcao}</p>}
                      {c.telefone && (
                        <p className="mt-0.5 text-[12px] text-foreground-500">{formatarTelefone(c.telefone)}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {situacao === 'aguardando' && <Chip tipo="warning">Ainda não abriu</Chip>}
                      {situacao === 'aceito' && como === 'link' && (
                        <Chip tipo="success">No celular desde {horaBrasilia(q?.no_celular_desde)}</Chip>
                      )}
                      {situacao === 'aceito' && como === 'conta pessoal' && (
                        <Chip tipo="info">Pela conta pessoal</Chip>
                      )}
                      {situacao === 'aceito' && !como && <Chip tipo="success">Aceito</Chip>}
                      {situacao === 'vencido' && <Chip tipo="neutro">Vencido</Chip>}
                      {situacao === 'cancelado' && <Chip tipo="neutro">Cancelado</Chip>}
                      {liberadoAgora && <Chip tipo="info">Liberado até {horaBrasilia(q?.liberado_ate)}</Chip>}
                    </div>
                  </div>

                  {situacao === 'aceito' && como === 'link' && q?.ultima_abertura && (
                    <p className="mt-1 text-[12px] text-foreground-500">
                      Última abertura {horaBrasilia(q.ultima_abertura)}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2">
                    {situacao === 'aguardando' && (
                      <>
                        <button
                          type="button"
                          onClick={() => enviarWhatsConvite(c)}
                          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-[#25D366] px-3 text-[13px] font-semibold text-white transition-colors hover:brightness-95"
                        >
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-whatsapp-line" />
                          </span>
                          WhatsApp
                        </button>
                        <button
                          type="button"
                          onClick={() => copiarConvite(c)}
                          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                        >
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className={copiado === c.convite_id ? 'ri-check-line' : 'ri-file-copy-line'} />
                          </span>
                          {copiado === c.convite_id ? 'Copiado ✓' : 'Copiar link'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCancelando(c)}
                          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-danger-300 bg-white px-3 text-[13px] font-semibold text-danger-600 transition-colors hover:bg-danger-50"
                        >
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-close-line" />
                          </span>
                          Cancelar
                        </button>
                      </>
                    )}
                    {como === 'link' && (
                      <button
                        type="button"
                        onClick={() => setLiberando(c)}
                        className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-300 bg-white px-3 text-[13px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-smartphone-line" />
                        </span>
                        Liberar outro celular
                      </button>
                    )}
                    {q?.e_caixa && situacao !== 'cancelado' && (
                      <button
                        type="button"
                        onClick={() => setTrocando(c)}
                        className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-exchange-line" />
                        </span>
                        Trocar caixa
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Cartao>
      )}

      {/* Direção (só leitura) */}
      <Cartao className="p-4">
        <h2 className="flex items-center gap-2 text-[16px] font-bold text-foreground-950">
          <span className="flex h-6 w-6 items-center justify-center text-secondary-500">
            <i className="ri-shield-user-line" />
          </span>
          Direção
        </h2>
        {direcao.filter((m) => m.status === 'ativo').length === 0 ? (
          <p className="mt-2 text-[13px] text-foreground-500">Ninguém na direção ainda.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {direcao
              .filter((m) => m.status === 'ativo')
              .map((m) => (
                <li key={m.id} className="flex items-center gap-2 py-1">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[12px] font-bold text-secondary-600">
                    {iniciais(m.pessoa ?? '', m.email ?? '')}
                  </span>
                  <span className="truncate text-[14px] text-foreground-800">
                    {m.pessoa || m.email}
                    {m.funcao ? ` · ${m.funcao}` : ''}
                  </span>
                </li>
              ))}
          </ul>
        )}
        <p className="mt-2 text-[12px] text-foreground-500">
          a direção é gerenciada em Mais › Quem está na direção
        </p>
      </Cartao>
        </>
      )}

      {chamar && (
        <FolhaChamar
          eventoId={eventoId}
          eventoNome={eventoNome}
          pontos={pontosAtivos}
          temLoja={temLoja}
          inicial={chamar}
          aoFechar={() => setChamar(null)}
          aoSalvo={recarregar}
          notificar={notificar}
        />
      )}

      {folhaPessoa && (
        <FolhaFuncoes
          nome={folhaPessoa.nome}
          email={folhaPessoa.email}
          funcoes={folhaPessoa.funcoes}
          aoFechar={() => setFolhaPessoa(null)}
          aoSalvo={() => {
            setFolhaPessoa(null);
            recarregar();
          }}
          notificar={notificar}
        />
      )}

      {cancelando && (
        <ModalConfirmar
          titulo="Cancelar convite?"
          mensagem={
            <>
              Cancelar o convite de <strong className="font-semibold">{cancelando.nome}</strong>? O link
              deixa de funcionar.
            </>
          }
          rotuloConfirmar="Cancelar convite"
          confirmando={cancelandoAgora}
          aoConfirmar={cancelarConvite}
          aoCancelar={() => setCancelando(null)}
        />
      )}

      {liberando && (
        <ModalConfirmar
          titulo="Liberar outro celular?"
          mensagem={
            <>
              O mesmo link vai abrir em outro celular nos próximos 30 minutos. Use quando a pessoa
              trocar de celular.
            </>
          }
          rotuloConfirmar="Liberar"
          confirmando={liberandoAgora}
          aoConfirmar={liberarOutroCelular}
          aoCancelar={() => setLiberando(null)}
        />
      )}

      {trocando && (
        <FolhaTrocarCaixa
          conviteId={trocando.convite_id}
          nome={trocando.nome}
          caixas={caixasAtivos}
          atual={quemPorConvite.get(trocando.convite_id)?.caixa_id ?? null}
          aoFechar={() => setTrocando(null)}
          aoSalvo={() => {
            setTrocando(null);
            recarregar();
          }}
          notificar={notificar}
        />
      )}
    </div>
  );
}